import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';
import { calculateOptionValueFromAPI } from '../services/optionPricing';
import './OptionChain.css';

// تابع دقیق محاسبه تعداد روز باقیمانده از تاریخ شمسی تا امروز
function getDaysLeft(jalaliDateStr) {
  if (!jalaliDateStr) return 0;
  
  const [jy, jm, jd] = jalaliDateStr.split('/').map(Number);
  const epbase = jy - (jy >= 0 ? 474 : 473);
  const epY = 474 + (epbase % 2820);
  const md = (jm - 1) * 31 - Math.floor(jm / 7) * (jm - 7);
  const targetJdn = jd + md + Math.floor((epY * 682 - 110) / 2816) + (epY - 1) * 365 + Math.floor(epbase / 2820) * 1029983 + (1948320 - 1);
  
  const today = new Date();
  const gy = today.getFullYear(), gm = today.getMonth() + 1, gd = today.getDate();
  const todayJdn = Math.floor((1461 * (gy + 4800 + Math.floor((gm - 14) / 12))) / 4) +
    Math.floor((367 * (gm - 2 - 12 * Math.floor((gm - 14) / 12))) / 12) -
    Math.floor((3 * Math.floor((gy + 4900 + Math.floor((gm - 14) / 12)) / 100)) / 4) + gd - 32075;
  
  const diff = targetJdn - todayJdn;
  return diff > 0 ? diff : 0;
}

// تابع محاسبه نرمال استاندارد برای فرمول بلک شولز
function CND(x) {
  const a1 = 0.31938153, a2 = -0.356563782, a3 = 1.781477937, a4 = -1.821255978, a5 = 1.330274429;
  const L = Math.abs(x);
  const K = 1.0 / (1.0 + 0.2316419 * L);
  let res = 1.0 - 1.0 / Math.sqrt(2 * Math.PI) * Math.exp(-L * L / 2) * (a1 * K + a2 * Math.pow(K, 2) + a3 * Math.pow(K, 3) + a4 * Math.pow(K, 4) + a5 * Math.pow(K, 5));
  if (x < 0) res = 1.0 - res;
  return res;
}

// تابع محاسبه بلک شولز
function calculateBlackScholes(S, K, T_days, r, sigma, type) {
  if (!S || !K || !T_days) return null;
  const T = T_days / 365;
  const d1 = (Math.log(S / K) + (r + Math.pow(sigma, 2) / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  if (type === 'call') {
    return S * CND(d1) - K * Math.exp(-r * T) * CND(d2);
  } else {
    return K * Math.exp(-r * T) * CND(-d2) - S * CND(-d1);
  }
}

// تبدیل رنگ هگزا به rgba با شفافیت
function hexToRgba(hex, alpha) {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function OptionChain() {
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');
  const [underlying, setUnderlying] = useState(null);
  const [options, setOptions] = useState([]);
  const [basePrices, setBasePrices] = useState({});
  const [loading, setLoading] = useState(false);

  // استیت‌های فیلتر و مرتب‌سازی
  const [filterType, setFilterType] = useState('all'); // options: all, call, put
  const [filterStatus, setFilterStatus] = useState('all'); // options: all, itm, otm
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // استیت‌های فیلترهای جدید
  const [filterDaysMin, setFilterDaysMin] = useState(0); // حداقل روز تا سررسید
  const [filterDaysMax, setFilterDaysMax] = useState(999); // حداکثر روز تا سررسید
  const [filterVolumeMin, setFilterVolumeMin] = useState(0); // حداقل حجم
  const [filterVolumeMax, setFilterVolumeMax] = useState(999999999); // حداکثر حجم
  const [filterSignal, setFilterSignal] = useState('all'); // all, buy, sell, neutral
  const [filterDiffMin, setFilterDiffMin] = useState(null); // حداقل اختلاف با سهم (%)
  const [filterDiffMax, setFilterDiffMax] = useState(null); // حداکثر اختلاف با سهم (%)

  // تابع پاک کردن همه فیلترها
  const clearAllFilters = () => {
    setFilterType('all');
    setFilterStatus('all');
    setFilterDaysMin(0);
    setFilterDaysMax(999);
    setFilterVolumeMin(0);
    setFilterVolumeMax(999999999);
    setFilterSignal('all');
    setFilterDiffMin(null);
    setFilterDiffMax(null);
    setOptionSearchQuery('');
  };

  const handleSearch = async (showLoading = true) => {
    if (!searchQuery) return;
    if (showLoading) setLoading(true);
    try {
      const data = await api.get(`/api/options-chain?base=${searchQuery}`);
      setUnderlying(data.underlying);
      setOptions(data.options);
      setBasePrices(data.base_prices || {});
    } catch (err) {
      console.error('Error fetching options:', err);
      alert('خطا در دریافت اطلاعات');
    }
    if (showLoading) setLoading(false);
  };

  // دریافت تمام آپشن‌های بازار در لود اولیه
  const fetchAllOptions = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const data = await api.get('/api/options-chain');
      setUnderlying(data.underlying);
      setOptions(data.options);
      setBasePrices(data.base_prices || {});
      setBasePrices(data.base_prices || {});
    } catch (err) {
      console.error('Error fetching all options:', err);
      alert('خطا در دریافت اطلاعات آپشن‌ها');
    }
    if (showLoading) setLoading(false);
  };

  useEffect(() => {
    fetchAllOptions();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (searchQuery) handleSearch(false);
      else fetchAllOptions(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [searchQuery]);

  const getStatus = (S, K, type) => {
    if (!S || !K) return '-';
    if (type === 'call') return S > K ? 'ITM (در سود)' : 'OTM (در ضرر)';
    if (type === 'put') return S < K ? 'ITM (در سود)' : 'OTM (در ضرر)';
    return '-';
  };

  // ۱. پیش‌محاسبه و غنی‌سازی داده‌های زنجیره آپشن برای فیلتر و سورت آسان‌تر
  // اگر underlying انتخاب شده باشد از آن استفاده می‌کند، در غیر این صورت از قیمت سهم پایه هر آپشن
  const enrichedOptions = useMemo(() => {
    return options.map((opt) => {
      const basePriceInfo = underlying
        ? { price: underlying.price, price_yesterday: underlying.price_yesterday, symbol: underlying.symbol }
        : basePrices[opt.base_symbol];
      const S = basePriceInfo?.price || 0;
      const S_yesterday = basePriceInfo?.price_yesterday || S;
      const actualDeltaS = S - S_yesterday;
      const baseSymbol = basePriceInfo?.symbol || opt.base_symbol || '';

      const K = opt.strike_price || 0;
      const T_days = getDaysLeft(opt.maturity_date);
      const status = getStatus(S, K, opt.type);
      const bsValue = calculateBlackScholes(S, K, T_days, 0.30, 0.40, opt.type);
      const bubble = bsValue ? parseFloat((((opt.price - bsValue) / bsValue) * 100).toFixed(2)) : null;
      const breakEven = opt.type === 'call' ? (K + opt.price) : (K - opt.price);
      const diffWithStock = S && K > 0 ? parseFloat((((breakEven - S) / S) * 100).toFixed(2)) : null;

      // محاسبه سیگنال
      let signal = '-';
      let signalColor = '#fff';
      if (bubble !== null) {
        if (bubble < -5) {
          signal = 'مستعد خرید';
          signalColor = '#4caf50';
        } else if (bubble > 5) {
          signal = 'مستعد فروش';
          signalColor = '#f44336';
        } else {
          signal = 'خنثی';
          signalColor = '#ffeb3b';
        }
      }

      // محاسبه قیمت عادلانه لحظه‌ای (Live Fair Value) - همان منطق StockAnalysis
      let liveFairValue = null;
      let liveFairValueRaw = null;
      let fairValueDiffPercent = null;
      let fairValueSignal = null;
      let fairValueSignalColor = null;

      if (S > 0 && K > 0 && opt.price > 0 && opt.maturity_date) {
        try {
          const optionResult = calculateOptionValueFromAPI(
            opt.symbol,
            S,
            opt.price,
            K,
            opt.maturity_date,
            baseSymbol,
            opt.type,
            opt.price_yesterday || 0,
            S_yesterday,
            0.40,
            0.30,
            actualDeltaS
          );

          if (optionResult?.predictions?.predictedTomorrowPrice !== 'N/A') {
            liveFairValueRaw = parseFloat(optionResult.predictions.predictedTomorrowPrice);
            if (isFinite(liveFairValueRaw)) {
              liveFairValue = Math.round(liveFairValueRaw);
              if (liveFairValueRaw > 0) {
                fairValueDiffPercent = ((opt.price - liveFairValueRaw) / liveFairValueRaw) * 100;
                if (Math.abs(fairValueDiffPercent) < 2) {
                  fairValueSignal = '⚖️ قیمت منصفانه';
                  fairValueSignalColor = '#bdbdbd';
                } else if (fairValueDiffPercent > 2 && fairValueDiffPercent <= 5) {
                  fairValueSignal = '🔴 حباب خفیف - احتیاط';
                  fairValueSignalColor = '#ef9a9a';
                } else if (fairValueDiffPercent > 5 && fairValueDiffPercent <= 10) {
                  fairValueSignal = '🔴 حباب قابل توجه - ریسک بالا';
                  fairValueSignalColor = '#e53935';
                } else if (fairValueDiffPercent > 10) {
                  fairValueSignal = '🔴 حباب شدید - بسیار گران';
                  fairValueSignalColor = '#c62828';
                } else if (fairValueDiffPercent < -2 && fairValueDiffPercent >= -5) {
                  fairValueSignal = '🟢 تخفیف خفیف - جذاب';
                  fairValueSignalColor = '#2e7d32';
                } else if (fairValueDiffPercent < -5 && fairValueDiffPercent >= -10) {
                  fairValueSignal = '🟢 تخفیف خوب - خرید';
                  fairValueSignalColor = '#66bb6a';
                } else if (fairValueDiffPercent < -10) {
                  fairValueSignal = '🟢 تخفیف عالی - خرید قوی';
                  fairValueSignalColor = '#a5d6a7';
                }
              }
            }
          }
        } catch (e) {
          console.error('Error calculating live fair value for', opt.symbol, e);
        }
      }

      return {
        ...opt,
        daysLeft: T_days,
        status,
        bsValue,
        bubble,
        diffWithStock,
        signal,
        signalColor,
        liveFairValue,
        liveFairValueRaw,
        fairValueDiffPercent,
        fairValueSignal,
        fairValueSignalColor,
      };
    });
  }, [options, underlying]);

  // ۲. اعمال فیلترها و مرتب‌سازی روی داده‌ها
  const processedOptions = useMemo(() => {
    let result = [...enrichedOptions];

    // اعمال فیلتر نوع آپشن
    if (filterType !== 'all') {
      result = result.filter(opt => opt.type === filterType);
    }

    // اعمال فیلتر وضعیت آپشن (در سود / در ضرر)
    if (filterStatus !== 'all') {
      result = result.filter(opt => {
        if (filterStatus === 'itm') return opt.status.includes('سود');
        if (filterStatus === 'otm') return opt.status.includes('ضرر');
        return true;
      });
    }

    // اعمال فیلتر جستجوی نماد آپشن
    if (optionSearchQuery.trim()) {
      const q = optionSearchQuery.trim().toLowerCase();
      result = result.filter(opt => opt.symbol && opt.symbol.toLowerCase().includes(q));
    }

    // اعمال فیلتر سررسید (تعداد روز باقیمانده)
    result = result.filter(opt => {
      const days = opt.daysLeft || 0;
      return days >= filterDaysMin && days <= filterDaysMax;
    });

    // اعمال فیلتر حجم معاملات
    result = result.filter(opt => {
      const vol = opt.volume || 0;
      return vol >= filterVolumeMin && vol <= filterVolumeMax;
    });

    // اعمال فیلتر سیگنال
    if (filterSignal !== 'all') {
      result = result.filter(opt => {
        if (filterSignal === 'buy') return opt.signal === 'مستعد خرید';
        if (filterSignal === 'sell') return opt.signal === 'مستعد فروش';
        if (filterSignal === 'neutral') return opt.signal === 'خنثی';
        return true;
      });
    }

    // اعمال فیلتر اختلاف با سهم
    if (filterDiffMin !== null) {
      result = result.filter(opt => (opt.diffWithStock || 0) >= filterDiffMin);
    }
    if (filterDiffMax !== null) {
      result = result.filter(opt => (opt.diffWithStock || 0) <= filterDiffMax);
    }

    // اعمال مرتب‌سازی (Sorting)
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // مدیریت مقادیر خالی (Null یا Undefined)
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue, 'fa')
            : bValue.localeCompare(aValue, 'fa');
        } else {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }

    return result;
  }, [enrichedOptions, filterType, filterStatus, sortConfig, optionSearchQuery, filterDaysMin, filterDaysMax, filterVolumeMin, filterVolumeMax, filterSignal, filterDiffMin, filterDiffMax]);

  // تابع مدیریت مرتب‌سازی با کلیک روی هدر ستون‌ها
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // گرفتن نشانه گرافیکی وضعیت سورت برای هدر جدول
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  return (
    <div className="option-chain-container">
      <div className="header-section">
        <h1>زنجیره آپشن و تحلیل بلک‌شولز</h1>
        
        <div className="search-box">
          <input 
            className="search-input"
            type="text" 
            placeholder="نماد پایه (مثل: شستا)" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? 'در حال جستجو...' : 'جستجو و دریافت آپشن‌ها'}
          </button>
        </div>
      </div>

      {underlying && (
        <div className="underlying-info">
          <h2>نماد پایه: {underlying.symbol}</h2>
          <p>قیمت لحظه‌ای: <strong>{underlying.price.toLocaleString()}</strong> ریال</p>
          <p style={{ fontSize: '12px', opacity: 0.7 }}>آخرین بروزرسانی: {underlying.last_update}</p>
        </div>
      )}

      {/* بخش کنترل فیلترها */}
      {options.length > 0 && (
        <div className="filter-bar">
          <div className="filter-group">
            <label>نوع اختیار:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">همه</option>
              <option value="call">اختیار خرید</option>
              <option value="put">اختیار فروش</option>
            </select>
          </div>

          <div className="filter-group">
            <label>وضعیت سوددهی:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">همه</option>
              <option value="itm">در سود (ITM)</option>
              <option value="otm">در ضرر (OTM)</option>
            </select>
          </div>

          <div className="filter-group">
            <label>سررسید (روز):</label>
            <input
              type="number"
              className="filter-input-small"
              placeholder="از"
              value={filterDaysMin === 0 ? '' : filterDaysMin}
              onChange={(e) => setFilterDaysMin(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
              min="0"
            />
            <span style={{ margin: '0 5px', color: '#888' }}>تا</span>
            <input
              type="number"
              className="filter-input-small"
              placeholder="تا"
              value={filterDaysMax === 999 ? '' : filterDaysMax}
              onChange={(e) => setFilterDaysMax(e.target.value === '' ? 999 : parseInt(e.target.value) || 999)}
              min="0"
            />
          </div>

          <div className="filter-group">
            <label>حجم معاملات:</label>
            <input
              type="number"
              className="filter-input-small"
              placeholder="از"
              value={filterVolumeMin === 0 ? '' : filterVolumeMin}
              onChange={(e) => setFilterVolumeMin(e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
              min="0"
            />
            <span style={{ margin: '0 5px', color: '#888' }}>تا</span>
            <input
              type="number"
              className="filter-input-small"
              placeholder="تا"
              value={filterVolumeMax === 999999999 ? '' : filterVolumeMax}
              onChange={(e) => setFilterVolumeMax(e.target.value === '' ? 999999999 : parseInt(e.target.value) || 999999999)}
              min="0"
            />
          </div>

          <div className="filter-group">
            <label>سیگنال:</label>
            <select value={filterSignal} onChange={(e) => setFilterSignal(e.target.value)}>
              <option value="all">همه</option>
              <option value="buy">🟢 مستعد خرید</option>
              <option value="neutral">🟡 خنثی</option>
              <option value="sell">🔴 مستعد فروش</option>
            </select>
          </div>

          <div className="filter-group">
            <label>اختلاف با سهم (%):</label>
            <input
              type="number"
              className="filter-input-small"
              placeholder="از"
              value={filterDiffMin === null ? '' : filterDiffMin}
              onChange={(e) => setFilterDiffMin(e.target.value === '' ? null : parseFloat(e.target.value))}
            />
            <span style={{ margin: '0 5px', color: '#888' }}>تا</span>
            <input
              type="number"
              className="filter-input-small"
              placeholder="تا"
              value={filterDiffMax === null ? '' : filterDiffMax}
              onChange={(e) => setFilterDiffMax(e.target.value === '' ? null : parseFloat(e.target.value))}
            />
          </div>

          <div className="filter-group option-search-group">
            <label>جستجوی نماد آپشن:</label>
            <div className="option-search-input-wrapper">
              <span className="option-search-icon">🔎</span>
              <input
                className="option-search-input"
                type="text"
                placeholder="مثلاً ضخود5053"
                value={optionSearchQuery}
                onChange={(e) => setOptionSearchQuery(e.target.value)}
              />
              {optionSearchQuery && (
                <button
                  className="option-search-clear"
                  onClick={() => setOptionSearchQuery('')}
                  title="پاک کردن"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="filter-group">
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              🔄 پاک کردن فیلترها
            </button>
          </div>
        </div>
      )}

      {processedOptions.length > 0 ? (
        <div className="table-container">
          <table className="options-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => requestSort('symbol')}>
                  نماد آپشن <span className="sort-icon">{getSortIcon('symbol')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('type')}>
                  نوع <span className="sort-icon">{getSortIcon('type')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('price')}>
                  قیمت لحظه‌ای آپشن <span className="sort-icon">{getSortIcon('price')}</span>
                </th>
                <th className="sortable-header volume-header" onClick={() => requestSort('volume')}>
                  حجم معاملات <span className="sort-icon">{getSortIcon('volume')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('strike_price')}>
                  قیمت اعمال <span className="sort-icon">{getSortIcon('strike_price')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('daysLeft')}>
                  سررسید (روز باقیمانده) <span className="sort-icon">{getSortIcon('daysLeft')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('status')}>
                  وضعیت (ITM/OTM) <span className="sort-icon">{getSortIcon('status')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('bsValue')}>
                  ارزش تئوریک (بلک‌شولز) <span className="sort-icon">{getSortIcon('bsValue')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('bubble')}>
                  حباب (اختلاف) <span className="sort-icon">{getSortIcon('bubble')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('diffWithStock')}>
                  اختلاف با سهم <span className="sort-icon">{getSortIcon('diffWithStock')}</span>
                </th>
                <th className="sortable-header live-fair-value-header" onClick={() => requestSort('liveFairValue')}>
                  💰 قیمت عادلانه لحظه‌ای <span className="sort-icon">{getSortIcon('liveFairValue')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('fairValueDiffPercent')}>
                  سیگنال <span className="sort-icon">{getSortIcon('fairValueDiffPercent')}</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {processedOptions.map((opt) => (
                <tr key={opt.symbol}>
                  <td className="symbol-cell">{opt.symbol}</td>
                  <td className={opt.type === 'call' ? 'call-text' : 'put-text'}>
                    {opt.type === 'call' ? 'اختیار خرید' : 'اختیار فروش'}
                  </td>
                  <td>{opt.price.toLocaleString()}</td>
                  <td className="volume-cell">
                    {opt.volume && opt.volume > 0 ? opt.volume.toLocaleString() : '-'}
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {opt.strike_price > 0 ? opt.strike_price.toLocaleString() : '-'}
                  </td>
                  <td>
                    {opt.maturity_date ? (
                      <span style={{ fontSize: '14px' }}>
                        <span style={{ display: 'block', color: '#888', fontSize: '11px' }}>{opt.maturity_date}</span>
                        {opt.daysLeft} روز
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ fontWeight: 'bold', color: opt.status.includes('سود') ? '#4caf50' : '#f44336' }}>
                    {opt.status}
                  </td>
                  <td>{opt.bsValue ? Math.round(opt.bsValue).toLocaleString() : '-'}</td>
                  <td dir="ltr" style={{ color: opt.bubble > 0 ? '#f44336' : (opt.bubble < 0 ? '#4caf50' : '#ccc') }}>
                    {opt.bubble !== null ? `% ${opt.bubble}` : '-'}
                  </td>
                  <td dir="ltr" style={{ color: opt.diffWithStock > 0 ? '#4caf50' : (opt.diffWithStock < 0 ? '#f44336' : '#ccc') }}>
                    {opt.diffWithStock !== null ? `% ${opt.diffWithStock}` : '-'}
                  </td>
                  <td className="live-fair-value-cell">
                    {opt.liveFairValue !== null ? (
                      <span className="live-fair-value-amount">{opt.liveFairValue.toLocaleString()}</span>
                    ) : (
                      <span className="live-fair-value-unavailable">-</span>
                    )}
                  </td>
                  <td className="option-signal-cell">
                    {opt.fairValueSignal ? (
                      <div
                        className="option-signal-box"
                        style={{
                          backgroundColor: hexToRgba(opt.fairValueSignalColor, 0.15),
                          borderColor: opt.fairValueSignalColor,
                        }}
                      >
                        <span className="option-signal-text" style={{ color: opt.fairValueSignalColor }}>
                          {opt.fairValueSignal}
                        </span>
                        {opt.fairValueDiffPercent !== null && (
                          <span className="option-signal-diff" style={{ color: opt.fairValueSignalColor }}>
                            {` ${opt.fairValueDiffPercent > 0 ? '+' : ''}${opt.fairValueDiffPercent.toFixed(2)}%`}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="option-signal-unavailable">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        options.length > 0 && <p style={{ textAlign: 'center', marginTop: '20px', color: '#aaa' }}>هیچ آپشنی با این فیلترها یافت نشد.</p>
      )}
    </div>
  );
}
